import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { ImpresCatalogItem, ImpresSupplierOffer } from './impresTypes'

export type ImpresCartLine = {
  item: ImpresCatalogItem
  supplierName: ImpresSupplierOffer['supplierName']
  qty: number
}

type CartMap = Partial<Record<string, Partial<Record<ImpresSupplierOffer['supplierName'], ImpresCartLine>>>>

type ImpresCartContextValue = {
  cart: CartMap
  setQty: (item: ImpresCatalogItem, supplierName: ImpresSupplierOffer['supplierName'], qty: number) => void
  clearCart: () => void
  itemCount: number
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
}

const ImpresCartContext = createContext<ImpresCartContextValue | null>(null)

export function ImpresCartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartMap>({})
  const [drawerOpen, setDrawerOpen] = useState(false)

  const setQty = useCallback(
    (item: ImpresCatalogItem, supplierName: ImpresSupplierOffer['supplierName'], qty: number) => {
      setCart(prev => ({
        ...prev,
        [item.id]: { ...prev[item.id], [supplierName]: { item, supplierName, qty } },
      }))
    },
    [],
  )

  const clearCart = useCallback(() => setCart({}), [])

  const itemCount = useMemo(
    () =>
      Object.values(cart).reduce((sum, suppliers) => {
        const lines = Object.values(suppliers ?? {}) as (ImpresCartLine | undefined)[]
        return sum + lines.reduce((s, line) => s + (line && line.qty > 0 ? line.qty : 0), 0)
      }, 0),
    [cart],
  )

  const value: ImpresCartContextValue = {
    cart,
    setQty,
    clearCart,
    itemCount,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
  }

  return <ImpresCartContext.Provider value={value}>{children}</ImpresCartContext.Provider>
}

export function useImpresCart(): ImpresCartContextValue {
  const ctx = useContext(ImpresCartContext)
  if (!ctx) throw new Error('useImpresCart must be used within an ImpresCartProvider')
  return ctx
}
